import { tuning } from '@/config/tuning';

/**
 * ball.ts — PURE ball physics (no three.js, no track). The entity feeds in the
 * continuous ground height each step; everything else is plain math, so it's
 * deterministic and unit-testable.
 *
 * A bouncy sphere with gravity, a ground bounce, rolling resistance while it's
 * on the ground, and a hard cap on horizontal speed so a fast car can never
 * launch it across the map (it should roll a satisfying-but-controllable
 * distance and stay dribblable in front of you).
 */
export interface BallPhysicsState {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number; // vertical
  vz: number;
  radius: number;
}

export type BallParams = typeof tuning.ball;

export function createBallState(x: number, y: number, z: number, radius: number): BallPhysicsState {
  return { x, y, z, vx: 0, vy: 0, vz: 0, radius };
}

/** Advance the ball one fixed step given the ground height beneath it. */
export function ballStep(s: BallPhysicsState, groundY: number, p: BallParams, dt: number): void {
  // Gravity + integrate.
  s.vy -= p.gravity * dt;
  s.x += s.vx * dt;
  s.y += s.vy * dt;
  s.z += s.vz * dt;

  // Ground bounce + rolling resistance while grounded.
  const restY = groundY + s.radius;
  if (s.y <= restY) {
    s.y = restY;
    if (s.vy < 0) {
      s.vy = -s.vy * p.restitution;
      if (Math.abs(s.vy) < 1.0) s.vy = 0; // settle tiny bounces
    }
    const damp = Math.max(0, 1 - p.rollingResistance * dt);
    s.vx *= damp;
    s.vz *= damp;
  }

  // Cap horizontal speed.
  const hs = Math.hypot(s.vx, s.vz);
  if (hs > p.maxSpeed) {
    const k = p.maxSpeed / hs;
    s.vx *= k;
    s.vz *= k;
  }
}
